import { Injectable } from '@angular/core';
import { SessionService } from '@services/session/session.service';

const CONTEXT_KEY_SUFFIX = '---forUser';
const CONTEXT_KEY_REGEX = new RegExp(`${CONTEXT_KEY_SUFFIX}\\d+$`);

export abstract class BrowserStorageService {
  protected session_svc: SessionService;

  constructor(protected storage: Storage) {}

  setContext(session_svc: SessionService): void {
    this.session_svc = session_svc;
  }

  getItem<T>(key: string): T | undefined {
    const item = this.storage.getItem(key);
    if (!item || typeof item !== 'string') return undefined;

    try {
      const data = JSON.parse(item);
      if (typeof data !== 'object' || !data.values || typeof data.expires !== 'number')
        return undefined;

      if (data.expires > 0 && Date.now() > data.expires) {
        this.storage.removeItem(key);
        return undefined;
      }

      return data.values as T;
    } catch (error) {
      return undefined;
    }
  }

  setItem<T>(key: string, values: T, life_time: number = 0): void {
    const data: string = JSON.stringify({
      values,
      expires: life_time > 0 ? Date.now() + life_time : 0
    });

    this.storage.setItem(key, data);
  }

  removeItems(key?: string): void {
    if (this.isUserKey(key)) return;

    if (key) this.storage.removeItem(key);
    else
      Object.keys(this.storage).forEach((k) => {
        if (!this.isUserKey(k)) this.storage.removeItem(k);
      });
  }

  removeItemsWithKeyStartingWith(key: string): void {
    if (!key) return;

    Object.keys(this.storage).forEach((k) => {
      if (k.startsWith(key)) this.storage.removeItem(k);
    });
  }

  clearContextItems(): void {
    Object.keys(this.storage).forEach((key) => {
      if (this.isContextKey(key)) this.storage.removeItem(key);
    });
  }

  getContextItem<T>(key: string): T | undefined {
    const user_key: string | undefined = this.getUserKey(key);
    if (!user_key) return undefined;

    return this.getItem<T>(user_key);
  }

  setContextItem<T>(key: string, values: T, life_time: number = 0): void {
    const user_key: string | undefined = this.getUserKey(key);
    if (!user_key) return;

    this.setItem<T>(user_key, values, life_time);
  }

  removeContextItems(key?: string): void {
    if (!this.session_svc.hasUser()) return;

    const user_key: string | undefined = this.getUserKey(key);

    if (user_key) this.storage.removeItem(user_key);
    else
      Object.keys(this.storage).forEach((k) => {
        if (this.isUserKey(k)) this.storage.removeItem(k);
      });
  }

  removeContextItemsWithKeyStartingWith(key: string): void {
    if (!key) return;

    Object.keys(this.storage).forEach((k) => {
      if (k.startsWith(key) && this.isUserKey(k)) this.storage.removeItem(k);
    });
  }

  clear(): void {
    this.storage.clear();
  }

  private getUserKey(key: string | undefined | null): string | undefined {
    if (!key || !this.session_svc.hasUser()) return undefined;
    return `${key}${CONTEXT_KEY_SUFFIX}${this.session_svc.getUser()?.id}`;
  }

  private isUserKey(key: string | undefined | null): boolean {
    if (!key || !this.session_svc.hasUser()) return false;
    return key.endsWith(`${CONTEXT_KEY_SUFFIX}${this.session_svc.getUser()?.id}`);
  }

  private isContextKey(key: string): boolean {
    return Boolean(key && key.match(CONTEXT_KEY_REGEX));
  }
}

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService extends BrowserStorageService {
  constructor() {
    super(window.localStorage);
  }
}

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService extends BrowserStorageService {
  constructor() {
    super(window.sessionStorage);
  }
}
